import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { chatService } from '../services/chatService'
import { useCharacterChatStream } from './useCharacterChatStream'

/**
 * Orchestrates character chat: fetches/creates conversation, manages messages,
 * sends new messages, and handles streaming responses.
 *
 * @param {string} storyId
 * @param {string|null} characterId - null deactivates the hook
 * @param {string} playerSessionId
 */
export function useCharacterChat(storyId, characterId, playerSessionId) {
  const [messages, setMessages] = useState([])
  const [isSending, setIsSending] = useState(false)
  const [sendError, setSendError] = useState(null)

  // Fetch or create conversation when character is selected
  const {
    data: conversation,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['conversation', storyId, characterId],
    queryFn: () => chatService.getOrCreateConversation(playerSessionId, characterId),
    enabled: !!characterId && !!playerSessionId,
  })

  const chatId = conversation?.id || null

  // Subscribe to streaming for this conversation
  const { streamingMessage, clearStream } = useCharacterChatStream(chatId)

  // Initialize messages from conversation history when data arrives or character changes.
  // characterId is included so cached conversation data is re-applied after switching back
  // to a previously chatted character (same object reference won't re-trigger [conversation] alone).
  useEffect(() => {
    if (conversation?.threads) {
      setMessages(conversation.threads)
    } else {
      setMessages([])
    }
    setSendError(null)
    setIsSending(false)
  }, [conversation, characterId])

  // Auto-request character introduction when conversation is new (no history).
  // Sends an empty messages array so the backend generates a response from the
  // system prompt alone, which includes the greeting instruction.
  const introRequestedRef = useRef(null)
  useEffect(() => {
    if (
      conversation &&
      chatId &&
      (!conversation.threads || conversation.threads.length === 0) &&
      introRequestedRef.current !== chatId &&
      !isSending
    ) {
      introRequestedRef.current = chatId
      setIsSending(true)
      setSendError(null)
      chatService
        .sendMessage({ messages: [], chatId, characterId, storyId })
        .then((responseText) => {
          if (responseText) {
            setMessages((prev) => [
              ...prev,
              { role: 'assistant', content: responseText, metadata: { created_at: new Date().toISOString() } },
            ])
          }
          setIsSending(false)
        })
        .catch((err) => {
          setSendError(err)
          setIsSending(false)
        })
    }
  }, [conversation, chatId, characterId, storyId])

  // When streaming completes, clear the stream buffer
  useEffect(() => {
    if (streamingMessage?.complete) {
      clearStream()
    }
  }, [streamingMessage?.complete])

  const sendMessage = useCallback(
    async (text) => {
      if (!text.trim() || !chatId || isSending) return

      setSendError(null)
      setIsSending(true)

      const userMessage = { role: 'user', content: text.trim(), metadata: { created_at: new Date().toISOString() } }
      setMessages((prev) => [...prev, userMessage])

      try {
        // Only send the new message — the backend prepends conversation history
        const responseText = await chatService.sendMessage({
          messages: [userMessage],
          chatId,
          characterId,
          storyId,
        })
        // HTTP response contains the complete text; ActionCable provides live streaming.
        // Use HTTP as the definitive source (matches conversation client pattern).
        if (responseText) {
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: responseText, metadata: { created_at: new Date().toISOString() } },
          ])
        }
        setIsSending(false)
      } catch (err) {
        setSendError(err)
        setIsSending(false)
      }
    },
    [chatId, characterId, storyId, isSending]
  )

  return {
    messages,
    streamingMessage,
    isLoading,
    isSending,
    sendMessage,
    error: fetchError || sendError,
  }
}
